import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from '../AxiosInstance';
import { resetIdentity } from '../services/analytics';
import '../styles/v2.css';

/* ── Shared icon snippets ── */
const ICO_PLUS   = <svg className="v2-nav-icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>;
const ICO_LIST   = <svg className="v2-nav-icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" /></svg>;

const NAV_ITEMS = [
  {
    label: 'Menu',
    items: [
      /* 1. Overview */
      { label: 'Overview', to: '/new/dashboard', icon: <svg className="v2-nav-icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" /></svg> },

      /* 2. Insights */
      { label: 'Insights', to: '/new/dashboard/insights', icon: <svg className="v2-nav-icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" /></svg> },

      /* 3. Products */
      {
        label: 'Products', to: null,
        icon: <svg className="v2-nav-icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" /></svg>,
        children: [
          { label: 'Add Product',   to: '/new/dashboard/products/add',       icon: ICO_PLUS },
          { label: 'View Products', to: '/new/dashboard/products/view',      icon: ICO_LIST },
          { label: 'Favorites',     to: '/new/dashboard/products/favorites', icon: <svg className="v2-nav-icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.562.562 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" /></svg> },
        ],
      },

      /* 4. Units */
      {
        label: 'Units', to: null,
        icon: <svg className="v2-nav-icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6.429 9.75L2.25 12l4.179 2.25m0-4.5l5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0l4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0l-5.571 3-5.571-3" /></svg>,
        children: [
          { label: 'Add Unit',   to: '/new/dashboard/units/add',  icon: ICO_PLUS },
          { label: 'View Units', to: '/new/dashboard/units/view', icon: ICO_LIST },
        ],
      },

      /* 5. Inventory */
      {
        label: 'Inventory', to: null,
        icon: <svg className="v2-nav-icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" /></svg>,
        children: [
          { label: 'Add Stock',          to: '/new/dashboard/inventories/add',           icon: ICO_PLUS },
          { label: 'View Inventory',     to: '/new/dashboard/inventories/view',          icon: ICO_LIST },
          { label: 'Reorder Inventory',  to: '/new/dashboard/reorder',                   icon: <svg className="v2-nav-icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg> },
          { label: 'View Reorders',      to: '/new/dashboard/inventories/view-reorders', icon: <svg className="v2-nav-icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg> },
        ],
      },

      /* 6. Purchases */
      {
        label: 'Purchases', to: null,
        icon: <svg className="v2-nav-icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" /></svg>,
        children: [
          { label: 'Add Purchase',   to: '/new/dashboard/purchases/add',  icon: ICO_PLUS },
          { label: 'View Purchases', to: '/new/dashboard/purchases/view', icon: ICO_LIST },
        ],
      },

      /* 7. Suppliers */
      {
        label: 'Suppliers', to: null,
        icon: <svg className="v2-nav-icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>,
        children: [
          { label: 'Add Supplier',   to: '/new/dashboard/suppliers/add',  icon: ICO_PLUS },
          { label: 'View Suppliers', to: '/new/dashboard/suppliers/view', icon: ICO_LIST },
        ],
      },

      /* 8. Sales */
      {
        label: 'Sales', to: null,
        icon: <svg className="v2-nav-icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" /></svg>,
        children: [
          { label: 'Add Sale',   to: '/new/dashboard/sales/add',  icon: ICO_PLUS },
          { label: 'View Sales', to: '/new/dashboard/sales/view', icon: ICO_LIST },
        ],
      },

      /* 9. Transactions */
      { label: 'Transactions', to: '/new/dashboard/transactions', icon: <svg className="v2-nav-icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" /></svg> },

    ],
  },
];

function getInitials(name) {
  if (!name) return 'U';
  return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

export default function DashboardLayoutV2() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [role, setRole] = useState('');
  const [alertsOpen, setAlertsOpen]         = useState(false);
  const [alerts, setAlerts]                 = useState({ lowStock: [], noStock: [], missingSuppliers: [] });
  const [alertsLoading, setAlertsLoading]   = useState(false);
  const alertsPanelRef = useRef(null);

  const [expandedGroups, setExpandedGroups] = useState(() => {
    // Auto-expand whichever group owns the current path on first render
    const initial = new Set();
    NAV_ITEMS.forEach(group =>
      group.items.forEach(item => {
        if (item.children?.some(c => c.to === window.location.pathname)) {
          initial.add(item.label);
        }
      })
    );
    return initial;
  });
  const userMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const PAGE_TITLES = {
    '/new/dashboard': 'Overview',
    '/new/dashboard/sales/add': 'Add Sale',
    '/new/dashboard/sales/view': 'Sales',
    '/new/dashboard/units/add': 'Add Unit',
    '/new/dashboard/units/view': 'Units',
    '/new/dashboard/purchases/add': 'Add Purchase',
    '/new/dashboard/purchases/view': 'Purchases',
    '/new/dashboard/suppliers/add': 'Add Supplier',
    '/new/dashboard/suppliers/view': 'Suppliers',
    '/new/dashboard/inventories/add': 'Add Stock',
    '/new/dashboard/inventories/view': 'Inventory',
    '/new/dashboard/products/add': 'Add Product',
    '/new/dashboard/products/view': 'Products',
    '/new/dashboard/products/favorites': 'Favorite Products',
    '/new/dashboard/insights': 'Business Insights',
    '/new/dashboard/transactions': 'Transactions',
    '/new/dashboard/reorder': 'Reorder Inventory',
    '/new/dashboard/inventories/view-reorders': 'View Reorders',
    '/new/dashboard/sales/edit/:id': 'Edit Sale',
    '/new/dashboard/purchases/edit/:id': 'Edit Purchase',
    '/new/dashboard/units/edit/:id': 'Edit Unit',
    '/new/dashboard/products/edit/:id': 'Edit Product',
    '/new/dashboard/suppliers/edit/:id': 'Edit Supplier',
    '/new/dashboard/inventories/reconcile/:id': 'Reconcile Inventory',
  };
  const pageTitle = PAGE_TITLES[location.pathname] || 'Dashboard';

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUsername(decoded.username || '');
        setRole(decoded.role || '');
      } catch {}
    }
  }, []);

  // Auto-expand the group that owns the active child when route changes
  useEffect(() => {
    NAV_ITEMS.forEach(group =>
      group.items.forEach(item => {
        if (item.children?.some(c => c.to === location.pathname)) {
          setExpandedGroups(prev => new Set([...prev, item.label]));
        }
      })
    );
  }, [location.pathname]);

  const toggleGroup = (label) =>
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });

  const fetchAlerts = async () => {
    setAlertsLoading(true);
    try {
      const [lowRes, noStockRes, suppliersRes] = await Promise.allSettled([
        axiosInstance.get('/overview/below-threshold'),
        axiosInstance.get('/overview/without-inventory'),
        axiosInstance.get('/overview/missing-suppliers'),
      ]);
      setAlerts({
        lowStock:          lowRes.status       === 'fulfilled' ? (lowRes.value.data.productsBelowThreshold || [])      : [],
        noStock:           noStockRes.status   === 'fulfilled' ? (noStockRes.value.data.productsWithoutInventory || []) : [],
        missingSuppliers:  suppliersRes.status === 'fulfilled' ? (suppliersRes.value.data.productsWithMissingSuppliers || []) : [],
      });
    } catch {}
    finally { setAlertsLoading(false); }
  };

  useEffect(() => { fetchAlerts(); }, []);

  /* close alerts panel on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (alertsOpen && alertsPanelRef.current && !alertsPanelRef.current.contains(e.target)) {
        setAlertsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [alertsOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    resetIdentity();
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('shop_name');
    navigate('/new/login');
  };

  const initials = getInitials(username);

  const SidebarContent = (
    <>
      {/* Logo */}
      <div style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: 12, borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0 }}>
        <div style={{ width: 36, height: 36, borderRadius: 12, background: 'linear-gradient(135deg,#34d399,#059669)', boxShadow: '0 4px 14px rgba(16,185,129,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <span style={{ color: 'white', fontWeight: 900, fontSize: 13 }}>ZS</span>
        </div>
        <div>
          <p style={{ color: 'white', fontWeight: 700, fontSize: 14.5, lineHeight: 1, letterSpacing: '-0.02em' }}>ZambiaShoppe</p>
          <p style={{ color: 'rgba(16,185,129,0.6)', fontSize: 10, fontWeight: 500, marginTop: 3 }}>Inventory Platform</p>
        </div>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '12px', overflowY: 'auto' }}>
        {NAV_ITEMS.map((group) => (
          <div key={group.label} style={{ marginBottom: 4 }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.1em', padding: '8px 12px 6px' }}>
              {group.label}
            </p>
            {group.items.map((item) => {
              if (item.children) {
                const isOpen = expandedGroups.has(item.label);
                const hasActiveChild = item.children.some(c => c.to === location.pathname);
                return (
                  <div key={item.label}>
                    {/* Clickable parent toggle */}
                    <button
                      type="button"
                      onClick={() => toggleGroup(item.label)}
                      className="v2-nav-link"
                      style={{
                        width: '100%',
                        background: 'none',
                        border: 'none',
                        justifyContent: 'space-between',
                        color: hasActiveChild ? '#e2e8f0' : '#64748b',
                        fontWeight: hasActiveChild ? 600 : 500,
                        cursor: 'pointer',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {item.icon}
                        {item.label}
                      </span>
                      <svg
                        style={{ width: 14, height: 14, flexShrink: 0, transition: 'transform 0.2s ease', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                        fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                      </svg>
                    </button>
                    {/* Collapsible children */}
                    <div style={{
                      maxHeight: isOpen ? `${item.children.length * 44}px` : '0px',
                      overflow: 'hidden',
                      transition: 'max-height 0.22s ease',
                      paddingLeft: 14,
                      borderLeft: isOpen ? '1.5px solid rgba(255,255,255,0.07)' : '1.5px solid transparent',
                      marginLeft: 20,
                      marginBottom: isOpen ? 2 : 0,
                    }}>
                      {item.children.map((child) => (
                        <NavLink
                          key={child.label}
                          to={child.to}
                          end
                          className={({ isActive }) => `v2-nav-link${isActive ? ' active' : ''}`}
                          onClick={() => isMobile && setSidebarOpen(false)}
                          style={{ fontSize: 13 }}
                        >
                          {child.icon}
                          {child.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                );
              }
              return item.to ? (
                <NavLink
                  key={item.label}
                  to={item.to}
                  end
                  className={({ isActive }) => `v2-nav-link${isActive ? ' active' : ''}`}
                  onClick={() => isMobile && setSidebarOpen(false)}
                >
                  {item.icon}
                  {item.label}
                </NavLink>
              ) : (
                <span key={item.label} className="v2-nav-link" style={{ opacity: 0.45, cursor: 'not-allowed' }}>
                  {item.icon}
                  {item.label}
                </span>
              );
            })}
            <div style={{ height: 1, background: 'rgba(255,255,255,0.05)', margin: '8px 12px' }} />
          </div>
        ))}
      </nav>

      {/* User section */}
      <div style={{ padding: '12px', borderTop: '1px solid rgba(255,255,255,0.05)', flexShrink: 0, position: 'relative' }} ref={userMenuRef}>
        {userMenuOpen && (
          <div className="v2-user-dropdown">
            <button className="danger" onClick={handleLogout}>
              <svg style={{ width: 15, height: 15 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
              </svg>
              Sign out
            </button>
          </div>
        )}
        <button className="v2-user-menu-btn" style={{ width: '100%' }} onClick={() => setUserMenuOpen(o => !o)}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#34d399,#0d9488)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ color: 'white', fontSize: 11, fontWeight: 900 }}>{initials}</span>
          </div>
          <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            <p style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600, lineHeight: 1, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{username || 'User'}</p>
            <p style={{ color: '#475569', fontSize: 10 }}>{role || 'Member'}</p>
          </div>
          <svg style={{ width: 14, height: 14, color: '#475569', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
          </svg>
        </button>
      </div>
    </>
  );

  return (
    <div className="v2-root" style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#f0f4f2' }}>

      {/* Mobile backdrop */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 40 }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`v2-sidebar v2-sidebar-bg`}
        style={{
          width: 230,
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          overflowX: 'hidden',
          ...(isMobile ? {
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            zIndex: 50,
            transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
          } : {}),
        }}
      >
        {SidebarContent}
      </aside>

      {/* Main area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Header */}
        <header style={{
          height: 60,
          background: 'white',
          display: 'flex',
          alignItems: 'center',
          padding: '0 20px',
          gap: 12,
          flexShrink: 0,
          borderBottom: '1px solid #e8ede8',
        }}>
          {isMobile && (
            <button className="v2-hamburger" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
              <svg style={{ width: 20, height: 20 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            </button>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
            <h1 style={{ color: '#1e293b', fontWeight: 700, fontSize: 15, margin: 0 }}>{pageTitle}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 99, background: '#f0fdf4' }}>
              <div className="v2-pulse" style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} />
              <span style={{ color: '#065f46', fontSize: 10, fontWeight: 600 }}>Live</span>
            </div>
          </div>

          {/* Bell — alerts panel trigger */}
          {(() => {
            const totalAlerts = alerts.lowStock.length + alerts.noStock.length + alerts.missingSuppliers.length;
            return (
              <button onClick={() => setAlertsOpen(o => !o)}
                style={{ position: 'relative', padding: 8, borderRadius: 8, background: alertsOpen ? '#f0fdf4' : 'transparent', border: 'none', cursor: 'pointer', color: alertsOpen ? '#065f46' : '#64748b', display: 'flex', alignItems: 'center', transition: 'background 0.15s' }}>
                <svg style={{ width: 20, height: 20 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
                </svg>
                {totalAlerts > 0 && (
                  <span style={{ position: 'absolute', top: 4, right: 4, minWidth: 16, height: 16, borderRadius: 99, background: '#ef4444', color: 'white', fontSize: 9, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', border: '2px solid white', lineHeight: 1 }}>
                    {totalAlerts > 99 ? '99+' : totalAlerts}
                  </span>
                )}
              </button>
            );
          })()}

          {/* Avatar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#34d399,#0d9488)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: 'white', fontSize: 11, fontWeight: 900 }}>{initials}</span>
            </div>
            <div className="v2-header-username">
              <p style={{ color: '#334155', fontSize: 13, fontWeight: 600, lineHeight: 1, marginBottom: 2 }}>{username || 'User'}</p>
              <p style={{ color: '#94a3b8', fontSize: 10, lineHeight: 1 }}>{role || 'Member'}</p>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          <div style={{ padding: 20 }}>
            <Outlet context={{ username, role, greeting: getGreeting() }} />
          </div>
        </main>
      </div>

      {/* ── Alerts panel overlay backdrop ── */}
      {alertsOpen && (
        <div onClick={() => setAlertsOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 200, backdropFilter: 'blur(1px)' }} />
      )}

      {/* ── Alerts slide-in panel ── */}
      <div ref={alertsPanelRef} style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 201,
        width: Math.min(380, window.innerWidth),
        background: 'white',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.15)',
        display: 'flex', flexDirection: 'column',
        transform: alertsOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.25s ease',
      }}>
        {/* Panel header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1px solid #f1f5f1', flexShrink: 0 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Stock Alerts</h2>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: '#94a3b8' }}>
              {alerts.lowStock.length + alerts.noStock.length + alerts.missingSuppliers.length === 0
                ? 'All good — no issues found'
                : `${alerts.lowStock.length + alerts.noStock.length + alerts.missingSuppliers.length} issue${alerts.lowStock.length + alerts.noStock.length + alerts.missingSuppliers.length !== 1 ? 's' : ''} need attention`}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => { fetchAlerts(); }} disabled={alertsLoading}
              style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', cursor: alertsLoading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
              <svg style={{ width: 14, height: 14, animation: alertsLoading ? 'spin 0.8s linear infinite' : 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
            </button>
            <button onClick={() => setAlertsOpen(false)}
              style={{ width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
              <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Panel body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {alertsLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 0', gap: 10 }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #e2e8f0', borderTopColor: '#10b981', animation: 'spin 0.8s linear infinite' }} />
              <span style={{ fontSize: 13, color: '#94a3b8' }}>Checking stock…</span>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : alerts.lowStock.length + alerts.noStock.length + alerts.missingSuppliers.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 0', gap: 12 }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg style={{ width: 28, height: 28, color: '#10b981' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', margin: 0 }}>All stock levels healthy</p>
              <p style={{ fontSize: 13, color: '#94a3b8', margin: 0, textAlign: 'center' }}>No issues detected. Check back after your next sales session.</p>
            </div>
          ) : (
            <>
              {/* Low stock */}
              {alerts.lowStock.length > 0 && (
                <AlertSection
                  title="Low Stock"
                  count={alerts.lowStock.length}
                  dotColor="#fbbf24"
                  bgColor="#fffbeb"
                  borderColor="#fde68a"
                  labelColor="#92400e"
                  items={alerts.lowStock}
                  renderItem={(p) => ({
                    name: formatAlertProduct(p),
                    sub: 'Below reorder threshold — needs restocking',
                    link: '/new/dashboard/reorder',
                    linkLabel: 'Reorder →',
                  })}
                  onNavigate={(link) => { navigate(link); setAlertsOpen(false); }}
                />
              )}

              {/* No stock set */}
              {alerts.noStock.length > 0 && (
                <AlertSection
                  title="No Initial Stock Set"
                  count={alerts.noStock.length}
                  dotColor="#ef4444"
                  bgColor="#fef2f2"
                  borderColor="#fecaca"
                  labelColor="#b91c1c"
                  items={alerts.noStock}
                  renderItem={(p) => ({
                    name: formatAlertProduct(p),
                    sub: 'Initial stock has never been entered',
                    link: `/new/dashboard/inventories/add?product_id=${p.productId}&product_name=${encodeURIComponent(p.productName || '')}&variety=${encodeURIComponent(p.variety || '')}`,
                    linkLabel: 'Set stock →',
                  })}
                  onNavigate={(link) => { navigate(link); setAlertsOpen(false); }}
                />
              )}

              {/* Missing suppliers */}
              {alerts.missingSuppliers.length > 0 && (
                <AlertSection
                  title="Missing Suppliers"
                  count={alerts.missingSuppliers.length}
                  dotColor="#f97316"
                  bgColor="#fff7ed"
                  borderColor="#fed7aa"
                  labelColor="#92400e"
                  items={alerts.missingSuppliers}
                  renderItem={(p) => ({
                    name: formatAlertProduct(p),
                    sub: 'Buying unit has no supplier linked',
                    link: p.missingUnitId ? `/new/dashboard/units/edit/${p.missingUnitId}` : '/new/dashboard/suppliers/view',
                    linkLabel: 'Add supplier →',
                  })}
                  onNavigate={(link) => { navigate(link); setAlertsOpen(false); }}
                />
              )}
            </>
          )}
        </div>

        {/* Panel footer */}
        <div style={{ padding: '14px 20px', borderTop: '1px solid #f1f5f1', flexShrink: 0 }}>
          <button onClick={() => { navigate('/new/dashboard/reorder'); setAlertsOpen(false); }}
            style={{ width: '100%', padding: '10px 0', borderRadius: 10, border: '1.5px solid #a7f3d0', background: '#f0fdf4', color: '#065f46', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            Go to Reorder Inventory →
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── helpers ── */
function formatAlertProduct(p) {
  let s = p.productName || p.product_name || '';
  if (p.variety) s += ` - ${p.variety}`;
  if (p.brand)   s += ` (${p.brand})`;
  return s;
}

function AlertSection({ title, count, dotColor, bgColor, borderColor, labelColor, items, renderItem, onNavigate }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{title}</span>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 99, background: bgColor, color: labelColor, border: `1px solid ${borderColor}` }}>{count}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.slice(0, 6).map((p, i) => {
          const { name, sub, link, linkLabel } = renderItem(p);
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', borderRadius: 10, background: bgColor, border: `1px solid ${borderColor}` }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: '#64748b' }}>{sub}</p>
              </div>
              <button onClick={() => onNavigate(link)}
                style={{ flexShrink: 0, padding: '4px 8px', borderRadius: 7, border: `1px solid ${borderColor}`, background: 'white', color: labelColor, fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {linkLabel}
              </button>
            </div>
          );
        })}
        {items.length > 6 && (
          <p style={{ margin: 0, fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: '4px 0' }}>
            +{items.length - 6} more
          </p>
        )}
      </div>
    </div>
  );
}
